import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Lightbulb as GuidanceIcon,
  Psychology as StrategyIcon
} from '@mui/icons-material';
import * as supervisorApi from '../../services/supervisorApi';
import therapistApi from '../../services/therapistApi';

function CareNotesManager({ 
  open, 
  onClose, 
  consultationId, 
  clientId,
  onCareNoteCreated, 
  initialContent = '', 
  token,
  editMode = false,
  noteId = null,
  initialType = 'guidance',
  initialPriority = 'medium',
  initialApplicableToAI = true,
  initialApplicableToHuman = true
}) {
  const [noteType, setNoteType] = useState(initialType);
  const [content, setContent] = useState(initialContent);
  const [priority, setPriority] = useState(initialPriority);
  const [applicableToAI, setApplicableToAI] = useState(initialApplicableToAI);
  const [applicableToHuman, setApplicableToHuman] = useState(initialApplicableToHuman);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset form when dialog opens with new content
  React.useEffect(() => {
    if (open && initialContent) {
      setContent(initialContent);
    }
  }, [open, initialContent]);

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Care note content is required');
      return;
    }

    if (!applicableToAI && !applicableToHuman) {
      setError('Care note must be applicable to at least one recipient');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const careNote = {
        type: noteType,
        content: content.trim(),
        priority,
        applicableToAI,
        applicableToHuman
      };

      let response;
      
      if (editMode && noteId) {
        // Update existing care note using therapist API
        response = await therapistApi.updateCareNote(noteId, careNote, token);
      } else if (consultationId) {
        // Create care note for consultation
        response = await supervisorApi.createCareNote(consultationId, careNote, token);
      } else if (clientId) {
        // Create standalone care note for client
        response = await therapistApi.createClientCareNote(clientId, careNote, token);
      } else {
        throw new Error('Missing required IDs for care note operation');
      }
      
      if (onCareNoteCreated) {
        onCareNoteCreated(response.careNote);
      }

      // Reset form only if creating new
      if (!editMode) {
        setContent('');
        setNoteType('guidance');
        setPriority('medium');
        setApplicableToAI(true);
        setApplicableToHuman(true);
      }
      
      onClose();
    } catch (err) {
      console.error('Error creating care note:', err);
      setError(err.response?.data?.msg || 'Failed to create care note');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setError('');
      onClose();
    }
  };

  const getNoteTypeIcon = (type) => {
    switch (type) {
      case 'warning':
        return <WarningIcon />;
      case 'strategy':
        return <StrategyIcon />;
      default:
        return <GuidanceIcon />;
    }
  };

  const getNoteTypeColor = (type) => {
    switch (type) {
      case 'warning':
        return 'error';
      case 'strategy':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {editMode ? 'Edit' : 'Create'} Care Note
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={isSaving}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Note Type Selection */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Note Type</FormLabel>
          <RadioGroup
            row
            value={noteType}
            onChange={(e) => setNoteType(e.target.value)}
          >
            <FormControlLabel
              value="guidance"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <GuidanceIcon color="primary" />
                  <Typography>Guidance</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="warning"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon color="error" />
                  <Typography>Warning</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="strategy"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <StrategyIcon color="secondary" />
                  <Typography>Strategy</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Content */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Care Note Content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={
            noteType === 'warning' 
              ? "Describe what to avoid or be cautious about..."
              : noteType === 'strategy'
              ? "Describe the therapeutic strategy or approach..."
              : "Provide guidance for treatment..."
          }
          margin="normal"
          required
        />

        {/* Priority */}
        <FormControl component="fieldset" sx={{ mt: 2, mb: 2 }}>
          <FormLabel component="legend">Priority</FormLabel>
          <RadioGroup
            row
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <FormControlLabel
              value="high"
              control={<Radio />}
              label={<Chip label="High" color="error" size="small" />}
            />
            <FormControlLabel
              value="medium"
              control={<Radio />}
              label={<Chip label="Medium" color="warning" size="small" />}
            />
            <FormControlLabel
              value="low"
              control={<Radio />}
              label={<Chip label="Low" color="success" size="small" />}
            />
          </RadioGroup>
        </FormControl>

        {/* Applicability */}
        <FormControl component="fieldset">
          <FormLabel component="legend">Apply this care note to:</FormLabel>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applicableToAI}
                  onChange={(e) => setApplicableToAI(e.target.checked)}
                />
              }
              label="AI Therapist"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={applicableToHuman}
                  onChange={(e) => setApplicableToHuman(e.target.checked)}
                />
              }
              label="Human Therapist"
            />
          </Box>
        </FormControl>

        {/* Preview */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Preview:
          </Typography>
          <Box display="flex" alignItems="flex-start" gap={1}>
            {getNoteTypeIcon(noteType)}
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Chip 
                  label={noteType.toUpperCase()} 
                  size="small" 
                  color={getNoteTypeColor(noteType)}
                />
                <Chip 
                  label={priority.toUpperCase()} 
                  size="small" 
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  For: {applicableToAI && applicableToHuman ? 'AI & Human' : 
                       applicableToAI ? 'AI Only' : 'Human Only'}
                </Typography>
              </Box>
              <Typography variant="body2">
                {content || '(Your care note content will appear here)'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || !content.trim()}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? 'Creating...' : 'Create Care Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CareNotesManager;